import React, { useState, useRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Header from "../../../layouts/Header";
import AddRowModal from "./AddRowModal";
import BulkUploadModal from "./BulkUploadModal";
import { axios_instance } from "../../../api/axios_instance.js";
import BulkBankPaymentTable from "../../../layouts/BulkBankPaymentTable.jsx";
import RefreshSpin from "../../../layouts/RefreshSpin.jsx";
import { formatAmount } from "../../../utils/format.js";
import { resetBulkBankPaymentSortingColumns } from "../../../utils/constant.js";
import ConfirmationModal from "./ConfirmationModal.jsx";
import { ToastContainer, toast } from "react-toastify";
import { useCallback } from "react";
import { useMemo } from "react";
import {
  decryptRequest,
  encryptRequest,
} from "../../../utils/encryptdecryptrequest";
import MultiSelectDropDownReset from "../../../layouts/MultiSelectDropdownReset";

const BankBulkUpdate = () => {
  const initialFilterValues = useMemo(
    () => ({
      regno: "",
      date_from: "",
      date_to: "",
      banks: [],
      executives: [],
      cities: [],
    }),
    []
  );

  const initialDropdownState = useMemo(
    () => ({
      banks: [],
      executives: [],
      cities: [],
    }),
    []
  );

  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [dropdownData, setDropdownData] = useState(initialDropdownState);

  const [showArrowTopButton, setShowArrowTopButton] = useState(false);
  const [sortingColumns, setSortingColumns] = useState({
    ...resetBulkBankPaymentSortingColumns,
  });
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [showAddRowModal, setShowAddRowModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [visibilityObj, setVisibilityObj] = useState({});
  const [caseDetails, setCaseDetails] = useState({ caseData: [], tabCount: 0 });
  const [currentBalance, setCurrentBalance] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [bulkbankpaymentcheckboxes, setBulkbankpaymentcheckboxes] = useState(
    {}
  );
  const [confirmationState, setConfirmationState] = useState({
    confirmationData: [],
    confirmationMessage: "",
    confirmationAction: "",
  });

  const [addRowProps, setAddRowProps] = useState({});
  const [selectedSortingCol, setSelectedSortingCol] = useState({
    lead_id: -1,
  });

  const dropdownContainer = useRef(null);
  const dropdownContainer2 = useRef(null);
  const dropdownContainer3 = useRef(null);

  const filterValuesRef = useRef(filterValues);

  const dropdownContainers = useMemo(
    () => [dropdownContainer, dropdownContainer2, dropdownContainer3],
    [dropdownContainer, dropdownContainer2, dropdownContainer3]
  );

  const closeAllDropdowns = useCallback(
    (target) => {
      dropdownContainers
        .filter((container) => container !== target)
        .forEach((container) => {
          const ul = container.current;
          if (ul && ul.classList.contains("visible")) {
            ul.classList.remove("visible");
            ul.style.display = "none";
          }
        });
    },
    [dropdownContainers]
  );

  const fetchVisibilityData = useCallback(async () => {
    try {
      const resp = await axios_instance({
        url: "/rbac/getBulkBankPaymentVisibility",
        method: "POST",
        data: {},
      });
      setVisibilityObj((prevState) => ({ ...prevState, ...resp.data }));
    } catch (error) {}
  }, []);

  const fetchListData = useCallback(
    async (closeConfirmationModal, closeAddRowModal) => {
      if (closeConfirmationModal) {
        setShowConfirmationModal(false);
      }
      if (closeAddRowModal) {
        setShowAddRowModal(false);
      }

      setIsLoading(true);

      const encryptedData = encryptRequest({
        filterValues: filterValuesRef.current,
        sortingColumns: selectedSortingCol,
        page,
      });

      try {
        const res = await axios_instance({
          url: "/bulkbank/getBulkPaymentList",
          method: "POST",
          data: { reqData: encryptedData },
        });

        const {
          pageOfResults: caseData,
          counts: tabCount,
          current_balance,
        } = decryptRequest(res.data.data);

        setCaseDetails((prevState) => ({
          ...prevState,
          caseData,
          tabCount,
        }));

        setCurrentBalance(current_balance);
      } catch (error) {
        if (error.response && error.response.status !== 500) {
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, selectedSortingCol]
  );

  const fetchDropDownData = useCallback(async () => {
    const caseresponse = await axios_instance({
      url: "/bulkbank/fetchBulkBankDropdown",
      method: "GET",
    });

    const { banks, executives, cities } = decryptRequest(
      caseresponse.data.data
    );

    setDropdownData((prevState) => ({
      ...prevState,
      banks,
      executives,
      cities,
    }));
  }, []);

  const handleDocumentClick = useCallback(
    (e) => {
      // Check if the click was outside the dropdown container
      if (
        !dropdownContainers.some(
          (container) =>
            container.current && container.current.contains(e.target)
        )
      ) {
        closeAllDropdowns();
      }
    },
    [closeAllDropdowns, dropdownContainers]
  );

  useEffect(() => {
    fetchListData();
    document.addEventListener("click", handleDocumentClick);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [fetchListData, handleDocumentClick]);

  useEffect(() => {
    fetchDropDownData();
    fetchVisibilityData();
  }, [fetchDropDownData, fetchVisibilityData]);

  const handleCheckboxChange = (e, filterName) => {
    const { name, checked, value } = e.target;
    let temp = null;

    if (name === "selectall") {
      if (checked)
        temp = dropdownData[filterName].map(({ value }) => parseInt(value));
      else temp = [];
    } else {
      temp = [...filterValues[filterName]];

      if (checked) temp.push(parseInt(value));
      else temp = temp.filter((elem) => elem !== parseInt(value));
    }

    setFilterValues((prevState) => {
      const newFilterValues = {
        ...prevState,
        [filterName]: temp,
      };

      filterValuesRef.current = newFilterValues;
      return newFilterValues;
    });
  };

  const handleBulkBankPaymentCheckboxChange = (e, id) => {
    const { name, checked } = e.target;

    const data = {};
    if (name === "selectall") {
      caseDetails.caseData.forEach(({ bulk_payment_id }) => {
        data[bulk_payment_id] = checked;
      });
    } else {
      data[id] = checked;
      setBulkbankpaymentcheckboxes((prevState) => ({
        ...prevState,
        [id]: checked,
      }));
    }

    setBulkbankpaymentcheckboxes((prevState) => ({
      ...prevState,
      ...data,
    }));
  };

  const handleScroll = () => {
    if (window.scrollY > 100) setShowArrowTopButton(true);
    else setShowArrowTopButton(false);
  };

  const handleInputChange = (inputName, value) => {
    setFilterValues((prevFilterValues) => {
      const newFilterValues = {
        ...prevFilterValues,
        [inputName]: value,
      };

      filterValuesRef.current = newFilterValues;
      return newFilterValues;
    });
  };

  const handleSorting = async (filterName) => {
    let col = sortingColumns;
    const order = [0, -1].includes(col[filterName]) ? 1 : -1;

    col = { ...resetBulkBankPaymentSortingColumns };
    col[filterName] = order;
    setSortingColumns(col);

    setSelectedSortingCol({ [filterName]: order });
  };

  const toggleDropDown = (container, e) => {
    closeAllDropdowns(container);
    const ul = container.current;
    if (ul.classList.contains("visible")) {
      ul.classList.remove("visible");
      ul.style.display = "none";
    } else {
      ul.classList.add("visible");
      ul.style.display = "block";
    }
    e.stopPropagation();
  };

  const [resetClicked, setResetClicked] = useState(false);
  useEffect(() => {
    if (resetClicked) {
      setFilterValues(() => {
        const newFilterValues = initialFilterValues;
        filterValuesRef.current = newFilterValues;

        return newFilterValues;
      });
      setSortingColumns({ ...resetBulkBankPaymentSortingColumns });
      setPage(1);

      setBulkbankpaymentcheckboxes({});
      setSelectedSortingCol({ lead_id: -1 });
      setResetClicked(false);
    }
  }, [resetClicked, initialFilterValues]);

  const resetFunc = async (e) => {
    e.preventDefault();
    setResetClicked(true);
  };

  const pageSelected = (e, pageNo) => {
    e.preventDefault();
    setPage(pageNo);

    setBulkbankpaymentcheckboxes({});
  };

  const prevPage = (e) => {
    e.preventDefault();
    setPage((prevPage) => prevPage - 1);

    setBulkbankpaymentcheckboxes({});
  };

  const nextPage = (e) => {
    e.preventDefault();
    setPage((prevPage) => prevPage + 1);

    setBulkbankpaymentcheckboxes({});
  };

  const countSelectedCheckboxes = () => {
    let count = 0;
    for (let val of Object.values(bulkbankpaymentcheckboxes)) {
      if (val) count++;
    }
    return count;
  };

  const openConfirmationModal = (data, message, action) => {
    toast.dismiss();
    if (!data.length) {
      setShowAddRowModal(false);
      setShowBulkUploadModal(false);
      setShowConfirmationModal(false);

      return toast.error("No Record Found", { theme: "colored" });
    }

    setShowAddRowModal(false);
    setShowBulkUploadModal(false);
    setShowConfirmationModal(true);

    setConfirmationState((prevState) => ({
      confirmationData: data,
      confirmationMessage: message,
      confirmationAction: action,
    }));

    setBulkbankpaymentcheckboxes({});
  };

  const deleteBulkPayment = async () => {
    let selectedLeads = [];
    for (let [key, value] of Object.entries(bulkbankpaymentcheckboxes)) {
      if (value) selectedLeads.push(key);
    }

    if (!selectedLeads.length) {
      return toast.error("Please select a record", { theme: "colored" });
    }

    if (window.confirm("Are you sure you want to delete the selected rows?")) {
      try {
        const result = await axios_instance({
          url: "/bulkbank/deleteBulkPayment",
          method: "POST",
          data: {
            leads: selectedLeads,
          },
        });

        if (result.status === 200) {
          setBulkbankpaymentcheckboxes({});
          await fetchListData();
          toast(
            <>
              <span className="glyphicon glyphicon-ok-sign"></span>
              Payment row successfully deleted
            </>
          );
        }
      } catch (error) {}
    }
  };

  const updateBulkPayment = async () => {
    const leads = [];
    for (let [key, value] of Object.entries(bulkbankpaymentcheckboxes)) {
      if (value) {
        const caseObj = caseDetails.caseData.find(
          (val) => +val.bulk_payment_id === +key
        );
        if (caseObj) leads.push(caseObj);
      }
    }

    if (!leads.length) {
      return toast.error("Please select a record with Loan ID", {
        theme: "colored",
      });
    }

    if (window.confirm("Are you sure you want to update the selected rows?")) {
      try {
        const result = await axios_instance({
          url: "/bulkbank/updateBulkPayment",
          method: "POST",
          data: {
            leads,
            action: "update_balance",
          },
        });

        if (result.status === 200) {
          setBulkbankpaymentcheckboxes({});
          await fetchListData();
          toast(
            <>
              <span className="glyphicon glyphicon-ok-sign"></span>
              Bulk Payment updated successfully
            </>
          );
        }
      } catch (error) {}
    }
  };

  const editBulkPayment = (
    bank,
    amount,
    date,
    loanId,
    bulk_payment_id,
    utr_narration,
    transaction_id
  ) => {
    setShowAddRowModal(true);
    setAddRowProps({
      bank,
      amount,
      date,
      loanId,
      bulk_payment_id,
      utr_narration,
      transaction_id,
    });
  };

  return (
    <span>
      <Header
        isNavigationModal={showNavigationModal}
        closeNavigationModal={() => setShowNavigationModal(false)}
        openNavigationModal={() => setShowNavigationModal(true)}
      ></Header>

      <AddRowModal
        closeAddRowModal={() => setShowAddRowModal(false)}
        addRowProps={addRowProps}
        openConfirmationModal={openConfirmationModal}
        fetchListData={fetchListData}
        showAddRowModal={showAddRowModal}
      />

      <BulkUploadModal
        closeBulkUploadModal={() => setShowBulkUploadModal(false)}
        openConfirmationModal={openConfirmationModal}
        showBulkUploadModal={showBulkUploadModal}
      />

      <ConfirmationModal
        closeConfirmationModal={() => setShowConfirmationModal(false)}
        confirmationData={confirmationState.confirmationData}
        confirmationMessage={confirmationState.confirmationMessage}
        confirmationAction={confirmationState.confirmationAction}
        fetchListData={fetchListData}
        showConfirmationModal={showConfirmationModal}
      />

      <ToastContainer
        closeOnClick={false}
        draggable={false}
        closeButton={false}
      />

      <div className="container-fluid">
        <div className="row">
          <div className="main_wrapper">
            <div className="overlay" id="navigation-overly"></div>
            <section className="right_wrapper">
              <div className="panel-white">
                <input type="hidden" id="headerSearch" value="" />
                <input type="hidden" id="tab_status" value="-1" />
                <input
                  type="hidden"
                  name="finance_lead_id"
                  id="finance_lead_id"
                  value=""
                />
                <input
                  type="hidden"
                  name="total_cases"
                  id="total_cases"
                  value=""
                />

                <div className="grayFiltr-bg brdr-FB clearfix">
                  <div className="col-md-12 ">
                    <h3 className="list_title pull-left">
                      Bulk Bank Payment Update
                    </h3>
                    <div className="pull-right">
                      <div className="pull-right font-18 crntbalance">
                        Current Balance -
                        <span
                          id="spanCurrentBal"
                          className={`font-md mrg-L10 ${
                            currentBalance < 0 ? "col-red" : "col-green"
                          }`}
                        >
                          <span className="fa fa-inr"></span>
                          {` ${formatAmount(currentBalance)}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="filter_flyout">
                  <div className="filter_box fm-filter filter-section-bx" id="">
                    <div className="row pad-L10">
                      <form id="form-filter-bulkpayment">
                        <div className="col-lg-3 col-md-3 col-sm-3 col-xs-12 pad-LR">
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-control search_icon pad-L30"
                              name="reg_no"
                              id="reg_no"
                              value={filterValues.regno}
                              placeholder="Loan ID, Customer Name, Reg No."
                              onChange={(e) =>
                                handleInputChange("regno", e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-2 col-md-2 col-sm-6 ms-drpdwn ">
                          <div className="row">
                            <div className="col-lg-6 col-md-6 col-sm-6 mrg-all-0 sm-text-box">
                              <div
                                className="input-append date input-group input-sm"
                                id="dp2"
                              >
                                <DatePicker
                                  selected={filterValues.date_from}
                                  onChange={(date) =>
                                    handleInputChange("date_from", date)
                                  }
                                  maxDate={new Date()}
                                  placeholderText="Date From"
                                  className="span2 form-control add-on calender filter-tb date-bg_input"
                                  dateFormat="dd-MM-yyyy"
                                />
                              </div>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-6 mrg-all-0 sm-text-box">
                              <div
                                className="input-append date input-group input-sm"
                                id="dp3"
                              >
                                <DatePicker
                                  selected={filterValues.date_to}
                                  onChange={(date) =>
                                    handleInputChange("date_to", date)
                                  }
                                  maxDate={new Date()}
                                  placeholderText="Date To"
                                  className="span2 form-control add-on calender filter-tb date-bg_input"
                                  dateFormat="dd-MM-yyyy"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-3 col-sm-6 ms-drpdwn pad-LR">
                          <div className="row">
                            <div className="col-lg-6 col-md-6 col-sm-7 mrg-all-0 pad-R5">
                              <MultiSelectDropDownReset
                                data={dropdownData.banks}
                                handleCheckboxChange={handleCheckboxChange}
                                toggleDropDown={toggleDropDown}
                                dropdownContainer={dropdownContainer}
                                title="Select Bank"
                                filterName="banks"
                                selectedDropdownData={filterValues.banks}
                              ></MultiSelectDropDownReset>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-5 mrg-all-0 pad-L5 ms-drpdwn ">
                              <MultiSelectDropDownReset
                                data={dropdownData.executives}
                                handleCheckboxChange={handleCheckboxChange}
                                toggleDropDown={toggleDropDown}
                                dropdownContainer={dropdownContainer2}
                                title="Executive Name"
                                filterName="executives"
                                selectedDropdownData={filterValues.executives}
                              ></MultiSelectDropDownReset>
                            </div>
                          </div>
                        </div>
                        {visibilityObj.showCityOptions ? (
                          <div className="col-lg-2 col-md-2 col-sm-6 ms-drpdwn pad-LR">
                            <div className="form-group">
                              <MultiSelectDropDownReset
                                data={dropdownData.cities}
                                handleCheckboxChange={handleCheckboxChange}
                                toggleDropDown={toggleDropDown}
                                dropdownContainer={dropdownContainer3}
                                title="Select City"
                                filterName="cities"
                                selectedDropdownData={filterValues.cities}
                              ></MultiSelectDropDownReset>
                            </div>
                          </div>
                        ) : (
                          ""
                        )}

                        <div className="col-lg-2 col-md-2 col-sm-3 pad-LR">
                          <div className="form-group">
                            <button
                              type="submit"
                              className="btn rc-btn_primary pad-L20 pad-R20 mar-R3 searchResults"
                              onClick={() => fetchListData()}
                              disabled={isLoading}
                            >
                              {isLoading ? "Please wait.." : "Search"}
                            </button>
                            <button
                              type="button"
                              className="btn rc-btn_rset pad-L20 pad-R20"
                              onClick={resetFunc}
                              disabled={isLoading}
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                <div className="container-fluid" id="list_data">
                  <div className="list-box mrg-T20 mrg-B20">
                    <div className="fixed-tabnavs filter_flyout dts_tab_navs">
                      <ul className="nav nav-tabs" role="tablist">
                        <li
                          data-input="0"
                          role="presentation"
                          className="active"
                        >
                          <a
                            href="#mycase"
                            aria-controls="My Cases"
                            role="tab"
                            data-toggle="tab"
                            data-status="0"
                          >
                            Bulk Bank Payment
                            <RefreshSpin
                              isLoading={isLoading}
                              count={caseDetails.tabCount}
                            />
                          </a>
                        </li>

                        <div className="pull-right font-18 fltBtngrp text-right filtergrouping">
                          {countSelectedCheckboxes() > 0 ? (
                            <div id="bulkBtngrp" className="text-right">
                              <button
                                className="btn btn-white mrg-R5"
                                onClick={deleteBulkPayment}
                              >
                                <i
                                  className="fa fa-trash-o"
                                  style={{ fontSize: "16px" }}
                                ></i>
                              </button>
                              <button
                                className="btn  btn-primary"
                                onClick={updateBulkPayment}
                              >
                                Update Selected
                              </button>
                            </div>
                          ) : (
                            <div id="selectedBtngrp" className="text-right">
                              <button
                                className="btn btn-white mrg-R5"
                                id="addRowBtn"
                                onClick={(e) => {
                                  setAddRowProps({});
                                  setShowAddRowModal(true);
                                  e.stopPropagation();
                                }}
                              >
                                Add Row
                              </button>
                              <button
                                className="btn btn-white mrg-R30"
                                id="bulkPopupBtn"
                                onClick={(e) => {
                                  setShowBulkUploadModal(true);
                                  e.stopPropagation();
                                }}
                              >
                                Bulk Upload
                              </button>
                            </div>
                          )}
                        </div>
                      </ul>
                    </div>

                    <div
                      className="tab-content ad-fx-cls"
                      style={{ marginTop: "0px" }}
                    >
                      {!isLoading ? (
                        <BulkBankPaymentTable
                          caseData={caseDetails.caseData}
                          bulkbankpaymentcheckboxes={bulkbankpaymentcheckboxes}
                          handleBulkBankPaymentCheckboxChange={
                            handleBulkBankPaymentCheckboxChange
                          }
                          sortingColumns={sortingColumns}
                          handleSorting={handleSorting}
                          page={page}
                          prevPage={prevPage}
                          nextPage={nextPage}
                          pageSelected={pageSelected}
                          editBulkPayment={editBulkPayment}
                        />
                      ) : (
                        <div
                          id="dtsMycase_processing"
                          className="dataTables_processing"
                          style={{ textAlign: "center" }}
                        >
                          <img
                            src={`${process.env.REACT_APP_STYLES_PATH}page-loader.gif`}
                            alt=""
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <div className="arrowTop">
        {showArrowTopButton ? (
          <div className="pageToup">
            <button
              id="go-top"
              onClick={() =>
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
              }
            >
              <span className="fa fa-arrow-up" aria-hidden="true"></span>
            </button>
          </div>
        ) : (
          ""
        )}
      </div>
    </span>
  );
};

export default BankBulkUpdate;
